import React, { useContext, useState } from 'react';
import { Stack } from '@mui/material';
import {
    ImportContactsOutlined,
    LibraryBooksOutlined,
} from '@mui/icons-material';
import SmallHeaderWithIconAndDescription from '@/operator/components/general/header/SmallHeaderWithIconAndDescription';
import { Invitation } from '@/operator/graphql/types/Invitation';
import UploadCsvFile from './UploadCsvFile';
import SendInvitationList from './SendInvitationList';
import ConfirmationModal from "Components/Common/ConfirmationModal";
import UserContext from "../../context/UsersContext";
import { useNotificationContext } from "app/contexts/NotificationContext";
import { InvitedUser, FirestoreUserRole, useSendEmailInviteMutation } from "generated/graphql";
import { InvitationType } from "../../constant/InvitationType.enum";
import { useAppSelector } from "hooks/index";
import { selectPartnerData, selectTeamsForTeamSelector } from "store/partner/partnerSelector";
import { selectUserData } from "store/user/userSelector";
import { DropdownOption } from "Interfaces/Dropdown/SelectDropdownInterfaces";

const SendCSVInvites: React.FC = () => {

    const { setSelectedInvitationType, addInvitation } = useContext(UserContext);
    const { showNotification } = useNotificationContext();

    const { uid: userId } = useAppSelector(selectUserData);
    const { instanceId: organizationId } = useAppSelector(selectPartnerData);
    const teams = useAppSelector(selectTeamsForTeamSelector);

    const teamOptions: DropdownOption[] = teams.map(({ name: teamLabel, key: teamValue }) => ({
        label: teamLabel,
        value: teamValue,
    }));

    const [sendUserInvitations, { loading }] = useSendEmailInviteMutation();

    const [showModal, setShowModal] = useState(true);
    const [invitationList, setInvitationList] = useState<Invitation[]>([]);
    const [csvFile, setCsvFile] = useState<File>();
    const [teamId, setTeamId] = useState(teamOptions[0]?.value || '');

    const getUserInstructions = () => {
        if (csvFile) {
            return (
                <SmallHeaderWithIconAndDescription
                    Icon={ImportContactsOutlined}
                    title="Awesome CSV"
                    description="Just need to hit that upload button now, and we’ll import your email addresses."
                    customStyle={{
                        marginTop: '31px',
                    }}
                />
            );
        }
        return (
            <SmallHeaderWithIconAndDescription
                Icon={LibraryBooksOutlined}
                title="There’s no one here"
                description="Upload a CSV (Comma Separated Values) of the email addresses you want to add."
                customStyle={{
                    marginTop: '31px',
                }}
            />
        );
    };

    const handleSendInvites = async () => {
        try {
            const createInviteInput: InvitedUser[] = invitationList.map(
                (invitation) => {
                    return {
                        email: invitation.email,
                        role: invitation.isAdmin
                            ? FirestoreUserRole.OrganizationAdmin
                            : FirestoreUserRole.User,
                    };
                },
            );

            const {
                data: { sendEmailInvite: invitesSent },
            } = await sendUserInvitations({
                variables: {
                    input: {
                        instanceId: organizationId,
                        inviterId: userId,
                        invitations: createInviteInput,
                        teamId
                    },
                },
            });
            addInvitation(invitesSent);
            showNotification({
                message: `We've sent ${invitationList.length} invitations`,
                severity: 'success',
            });
            handleOnCancel();
        } catch (error) {
            console.error(
                'something went wrong sending invitation list',
                error,
                error,
            );
            showNotification({
                message: 'Unable to send email invitation',
                severity: 'error',
            });
        }
    }

    const handleOnCancel = () => {
        setShowModal(false);
        setSelectedInvitationType(null);
    }

    return (
        <ConfirmationModal
            fullWidth
            open={showModal}
            onClose={handleOnCancel}
            title={InvitationType.InviteUsersViaCSV}
            content={
                <Stack spacing={3}>
                    <UploadCsvFile
                        setInvitationList={setInvitationList}
                        csvFile={csvFile}
                        setCsvFile={setCsvFile} />
                    {invitationList.length > 0 ? (
                        <SendInvitationList
                            invitationList={invitationList}
                            setInvitationList={setInvitationList}
                            teamId={teamId}
                            setTeamId={setTeamId}
                            teamOptions={teamOptions} />
                    ) : (
                        getUserInstructions()
                    )}
                </Stack>
            }
            footer={{
                primaryButton: {
                    label: 'Send Invites',
                    onClick: handleSendInvites,
                    disabled: !invitationList.length,
                    loading
                },
                secondaryButton: {
                    label: 'Cancel',
                    onClick: handleOnCancel,
                }
            }}
        />
    );
};

export default SendCSVInvites;
